import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcZoneService, StcZoneDto } from '../../../../../services/stcZoneService';
import { warehouseService } from '../../../../../services/warehouseService';
import { Warehouse } from '../../../../../types/warehouse';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { ZoneBulkUpdateModal } from './ZoneBulkUpdateModal';
import { ZoneCreateModal } from './ZoneCreateModal';

interface ZoneDataTableProps {
    refreshTrigger?: number;
}

export const ZoneDataTable: React.FC<ZoneDataTableProps> = ({
    refreshTrigger
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const companyNo = user?.companyNo || '';

    // Core data state
    const [data, setData] = useState<StcZoneDto[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedZoneIds, setSelectedZoneIds] = useState<number[]>([]);

    // Modal state
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Inline editing
    const inlineEditConfig = useInlineEdit<StcZoneDto>({
        getRowId: (row) => row.id,
        onSave: async (_originalRow, editedData) => {
            await stcZoneService.update(
                companyNo,
                editedData.warehouseID as string,
                editedData.stcZoneID as string,
                { description1: (editedData.description1 as string) || '' }
            );
        },
        onSaveSuccess: () => loadData(),
        onSaveError: (err) => setError(err.message || t('manager.modules.adminResources.zoneTable.errorSave', {}, 'Feil ved lagring'))
    });

    // Load data on mount and refresh
    useEffect(() => {
        loadData();
    }, [companyNo]);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = useCallback(async () => {
        if (!companyNo) return;

        setIsLoading(true);
        setError(null);

        try {
            // Fetch zones and warehouses in parallel
            const [zonesResult, warehousesResult] = await Promise.all([
                (async () => {
                    try {
                        return await stcZoneService.getCurrent();
                    } catch {
                        try {
                            return await stcZoneService.getAll();
                        } catch {
                            return [];
                        }
                    }
                })(),
                warehouseService.getWarehouses().catch(() => [] as Warehouse[])
            ]);

            setData(zonesResult);
            setWarehouses(warehousesResult);
        } catch (err: any) {
            console.error('Error loading zones:', err);
            setError(t('manager.modules.adminResources.zoneTable.errorLoad', {}, 'Kunne ikke laste lagersoner. Prøv igjen.'));
        } finally {
            setIsLoading(false);
        }
    }, [companyNo, t]);

    // Column factory
    const columnFactory = createColumnFactory<StcZoneDto>();

    // Format date for display
    const formatDate = (value: any): string => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('nb-NO', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return String(value);
        }
    };

    // Column definitions
    const columns: ColumnDefinition<StcZoneDto>[] = useMemo(() => [
        columnFactory.createTextColumn('stcZoneID', 'stcZoneID', t('manager.modules.adminResources.zone.zoneIdLabel', {}, 'Sone ID'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('warehouseID', 'warehouseID', t('manager.modules.adminResources.zone.warehouseIdLabel', {}, 'Lager ID'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
            minWidth: 250,
            sortable: true,
            filterType: 'both',
            editable: true
        }),
        columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminResources.zoneTable.colCreatedBy', {}, 'Opprettet av'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('created', 'created', t('manager.modules.adminResources.zoneTable.colCreated', {}, 'Opprettet'), {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        }),
        columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminResources.zoneTable.colUpdatedBy', {}, 'Endret av'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminResources.zoneTable.colLastUpdate', {}, 'Sist endret'), {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        })
    ], [t]);

    // Bulk edit handler
    const handleBulkEdit = useCallback(() => {
        if (selectedZoneIds.length === 0) {
            return;
        }
        setShowBulkEditModal(true);
    }, [selectedZoneIds.length]);

    // Handle modal success
    const handleUpdateSuccess = async () => {
        await loadData();
        setSelectedZoneIds([]);
        setShowBulkEditModal(false);
    };

    const handleDeleteSuccess = async () => {
        await loadData();
        setSelectedZoneIds([]);
        setShowBulkEditModal(false);
    };

    const handleCreateSuccess = async () => {
        await loadData();
        setShowCreateModal(false);
    };

    // Table actions
    const tableActions: TableAction<StcZoneDto>[] = useMemo(() => [
        {
            id: 'create',
            label: t('manager.modules.adminResources.zoneTable.newZone', {}, 'Ny lagersone'),
            icon: Plus,
            onClick: () => setShowCreateModal(true),
            variant: 'primary'
        },
        {
            id: 'bulk-edit',
            label: t('manager.modules.adminResources.common.maintenanceAction', { '0': selectedZoneIds.length }, `Vedlikehold (${selectedZoneIds.length})`),
            icon: Edit,
            onClick: () => handleBulkEdit(),
            disabled: () => selectedZoneIds.length === 0,
            variant: 'primary'
        }
    ], [selectedZoneIds.length, handleBulkEdit, t]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
            <div className="flex-1 min-h-0">
                <WarehouseTable
                    data={data}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: true,
                        showFilters: true,
                        showExport: true,
                        showPrint: true,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'zones-table',
                        allowRowSelection: {
                            mode: 'multi',
                            selectAllMode: 'all-data'
                        }
                    }}
                    selectedRows={selectedZoneIds}
                    onSelectionChange={(selectedIds) => {
                        setSelectedZoneIds(selectedIds as number[]);
                    }}
                    getRowId={(item) => item.id}
                    tableActions={tableActions}
                    inlineEditConfig={inlineEditConfig}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>

            {/* Bulk Update Modal */}
            {showBulkEditModal && selectedZoneIds.length > 0 && (
                <ZoneBulkUpdateModal
                    isOpen={showBulkEditModal}
                    onClose={() => setShowBulkEditModal(false)}
                    selectedZoneIds={selectedZoneIds}
                    data={data}
                    onUpdateSuccess={handleUpdateSuccess}
                    onDeleteSuccess={handleDeleteSuccess}
                    warehouses={warehouses}
                />
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <ZoneCreateModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                    warehouses={warehouses}
                />
            )}
        </div>
    );
};
